import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild} from '@angular/core';
import {SkillsProfileTopicDataResponseDto} from '../../../../../services/rest/api-types/SkillsProfileTopicDataResponseDto';
import {
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
    Chart,
    Plugin, LegendItem, ChartConfiguration, Scale
} from 'chart.js'
import {TranslateService} from '@ngx-translate/core';
import {CssColorsGetter} from '../../CssColorsGetter';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend,);

@Component({
    selector: 'mm-skill-profile-bar-diagram',
    templateUrl: './skill-profile-bar-diagram.component.html',
    styleUrl: './skill-profile-bar-diagram.component.scss',
    standalone: false
})
export class SkillProfileBarDiagramComponent implements AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('barCanvas')
    public barCanvas: ElementRef<HTMLCanvasElement> | undefined;

    @Input() topicsData: SkillsProfileTopicDataResponseDto[] = [];
    @Input() learningGoal: number | undefined;

    private barChart: Chart | undefined;
    private showGoalLine = true;
    private staticGridValues = [20, 40, 60, 80, 100];

    constructor(private host: ElementRef<HTMLElement>,
                private translate: TranslateService) {
    }

    public ngAfterViewInit(): void {
        if (this.topicsData !== undefined && this.topicsData.length > 0) {
            this.buildChart();
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if ((changes['topicsData'] !== undefined || changes['learningGoal'] !== undefined) && this.barCanvas !== undefined) {
            this.buildChart();
        }
    }

    public ngOnDestroy(): void {
        this.barChart?.destroy();
    }

    private barColors(values: number[], colors: { [key: string]: string }): string[] {
        return values.map(value => {
            if (this.learningGoal === undefined) {
                return colors.learningGoalReached;
            }
            return value >= this.learningGoal ? colors.learningGoalReached : colors.learningGoalNotReached
        });
    }

    private getCssColors(): { [key: string]: string } {
        const nativeElement = this.host.nativeElement;
        return {
            grid: CssColorsGetter.get(nativeElement, '--grid'),
            text: CssColorsGetter.get(nativeElement, '--text'),
            progress: CssColorsGetter.get(nativeElement, '--progress'),
            progressBackground: CssColorsGetter.get(nativeElement, '--progress-background'),
            goal: CssColorsGetter.get(nativeElement, '--goal'),
            learningGoalReached: CssColorsGetter.get(nativeElement, '--learning-goal-reached'),
            learningGoalReachedDark: CssColorsGetter.get(nativeElement, '--learning-goal-reached-dark'),
            learningGoalNotReached: CssColorsGetter.get(nativeElement, '--learning-goal-not-reached'),
            learningGoalNotReachedDark: CssColorsGetter.get(nativeElement, '--learning-goal-not-reached-dark'),
            learningGoalReachedBackground: CssColorsGetter.get(nativeElement, '--learning-goal-reached-background'),
            learningGoalNotReachedBackground: CssColorsGetter.get(nativeElement, '--learning-goal-not-reached-background'),
            labelBackground: CssColorsGetter.get(nativeElement, '--label-background')
        };
    }

    private get topicLabels(): string[] {
        return this.topicsData.map((topic) => {
            if (topic.isQuestionsWithoutTopic) {
                return this.translate.instant('learningGoal.insightsPopup.skillsProfile.questionsWithoutTopic');
            }
            return topic.topicName;
        });
    }

    private buildChart(): void {
        if (this.barCanvas === undefined || this.barCanvas.nativeElement === undefined) {
            return;
        }

        const learningProgressData = this.topicsData.map((t) => t.learningProgress);
        const colors = this.getCssColors();

        const config: ChartConfiguration<'bar'> = {
            type: 'bar',
            data: {
                labels: this.topicLabels,
                datasets: [{
                    label: this.translate.instant('learningGoal.insightsPopup.skillsProfile.questionsWithoutTopic'),
                    data: learningProgressData,
                    backgroundColor: this.barColors(learningProgressData, colors)
                }]
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                animation: false,
                scales: {
                    x: {
                        beginAtZero: true,
                        max: 100,
                        grid: {display: false},
                        border: {display: false},
                        ticks: {stepSize: 20},
                        title: {
                            display: true,
                            text: this.translate.instant('learningGoal.insightsPopup.skillsProfile.learningProgressLabel')
                        }
                    },
                    xLearningGoal: {
                        position: 'top',
                        beginAtZero: true,
                        max: 100,
                        title: {display: false},
                        border: {display: false},
                        ticks: {
                            color: colors.goal,
                            font: {weight: 'bold'},
                            stepSize: 5,
                            // eslint-disable-next-line no-null/no-null
                            callback: (value: string | number): string | null => value === this.learningGoal && this.showGoalLine ? `⚑ ${this.learningGoal} %` : null
                        }
                    },
                    y: {
                        grid: {display: false},
                        border: {display: false},
                        ticks: {font: {weight: 'normal'}}
                    }
                },
                plugins: {
                    legend: {
                        position: 'bottom',
                        align: 'center',
                        labels: {
                            padding: 28,
                            color: colors.text,
                            usePointStyle: true,
                            font: {size: 12, weight: 'normal'},
                            generateLabels: (chart): LegendItem[] => {
                                const legend: LegendItem[] = [];
                                const gradient = chart.ctx.createLinearGradient(0, 0, 0, 10);
                                gradient.addColorStop(0, 'red');    // top
                                gradient.addColorStop(1, 'green');
                                if (this.learningGoal !== undefined) {
                                    legend.push({
                                        text: this.translate.instant('learningGoal.insightsPopup.skillsProfile.learningGoalLabel'),
                                        fillStyle: colors.goal,
                                        strokeStyle: colors.goal,
                                        pointStyle: 'circle',
                                        index: 1,
                                        datasetIndex: 1,
                                        fontColor: colors.text,
                                        hidden: !this.showGoalLine
                                    })
                                }

                                return legend;
                            }
                        },
                        onClick: (e, legendItem, legend): void => {
                            const chart = legend.chart;
                            const learningGoalLabel = this.translate.instant('learningGoal.insightsPopup.skillsProfile.learningGoalLabel');

                            if (legendItem.text === learningGoalLabel) {
                                this.showGoalLine = !this.showGoalLine;
                            }
                            chart.update();
                        },
                        onHover: (event, legendItem, legend): void => {
                            const canvas = legend.chart.canvas;
                            if (canvas !== undefined) {
                                canvas.style.cursor = 'pointer';
                            }
                        },
                        onLeave: (event, legendItem, legend): void => {
                            const canvas = legend.chart.canvas;
                            if (canvas !== undefined) {
                                canvas.style.cursor = 'default';
                            }
                        }
                    },
                    tooltip: {
                        boxPadding: 8,
                        animation: false,
                        bodySpacing: 6,
                        titleFont: {size: 12, weight: 'bold'},
                        bodyFont: {size: 12, weight: 'normal'},
                        displayColors: false,
                        callbacks: {
                            label: (ctx): string[] => {
                                const topic = this.topicsData[ctx.dataIndex];
                                const learningProgressLabel = this.translate.instant('learningGoal.insightsPopup.skillsProfile.learningProgressTooltip');
                                const learningProgressInfo = `${learningProgressLabel}: ${topic.learningProgress} %`;
                                if (topic.numberOfQuestions === undefined) {
                                    return [learningProgressInfo];
                                }
                                const numberOfQuestionsLabel = this.translate.instant('learningGoal.insightsPopup.skillsProfile.numberOfQuestionsLabel');
                                const numberOfQuestionsInfo = `${numberOfQuestionsLabel}: ${topic.numberOfQuestions}`;
                                return [learningProgressInfo, numberOfQuestionsInfo];
                            }
                        }
                    }
                }
            },
            plugins: [this.getGoalBackgroundPlugin(colors), this.variableBarThicknessPlugin(), this.setWhiteBackgroundWhenSave()]
        };

        const ctx = this.barCanvas.nativeElement.getContext('2d')!;
        this.barChart = new Chart(ctx, config);
    }

    private setWhiteBackgroundWhenSave(): Plugin {
        return {
            id: 'whiteBackground',
            beforeDraw: (chart): void => {
                const ctx = chart.ctx;
                ctx.save();
                ctx.globalCompositeOperation = 'destination-over';
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, chart.width, chart.height);
                ctx.restore();
            }
        };
    }

    private getGoalBackgroundPlugin(colors: { [key: string]: string }): Plugin {
        return {
            id: 'learningGoalBackgroundPlugin',
            beforeDraw: (chart: Chart): void => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const {ctx, chartArea, scales} = chart as any;
                const xScale = scales.x;
                const yScale = scales.y;
                // eslint-disable-next-line no-null/no-null
                if (xScale === undefined || xScale === null || yScale === undefined || yScale === null || chartArea === undefined || chartArea === null) {
                    return;
                }
                const {left, right, top, bottom} = chartArea;

                ctx.save();
                if (this.learningGoal === undefined) {
                    // No goal set: fill entire background green
                    ctx.fillStyle = colors.learningGoalReachedBackground;
                    ctx.fillRect(left, top, right - left, bottom - top);
                } else {
                    const goalX = xScale.getPixelForValue(this.learningGoal);

                    // Red zone (below goal)
                    ctx.fillStyle = colors.learningGoalNotReachedBackground;
                    ctx.fillRect(left, top, Math.max(goalX - left, 0), bottom - top);

                    // Green zone (above goal)
                    ctx.fillStyle = colors.learningGoalReachedBackground;
                    ctx.fillRect(goalX, top, Math.max(right - goalX, 0), bottom - top);
                    ctx.restore();
                }

                this.drowGridLines(colors, ctx, xScale, top, bottom);
            }
        };
    }

    private drowGridLines(colors: { [p: string]: string }, ctx: CanvasRenderingContext2D, xScale: Scale, top: number, bottom: number): void {
        const gridValues = [...this.staticGridValues];
        if (this.learningGoal !== undefined && !this.staticGridValues.includes(this.learningGoal)) {
            gridValues.push(this.learningGoal);
        }
        for (const value of gridValues) {
            if(value === this.learningGoal && this.showGoalLine === false) {
                if(this.staticGridValues.includes(value)) {
                    this.drowGridLine(ctx, xScale, value, top, bottom, colors.grid);
                }
            } else{
                const color = value === this.learningGoal ? colors.goal : colors.grid;
                this.drowGridLine(ctx, xScale, value, top, bottom, color);
            }
        }
    }

    private drowGridLine(ctx: CanvasRenderingContext2D, xScale: Scale, value: number, top: number, bottom: number, color: string): void {
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(xScale.getPixelForValue(value), top);
        ctx.lineTo(xScale.getPixelForValue(value), bottom);
        ctx.stroke();
        ctx.restore();
    }

    private variableBarThicknessPlugin(): Plugin {
        return {
            id: 'variableBarThickness',
            beforeDatasetsDraw: (chart: Chart): boolean => {
                const ctx = chart.ctx;
                const dataset = chart.data.datasets[0];
                const meta = chart.getDatasetMeta(0);
                const thicknesses = ((): number[] => {
                    const first = this.topicsData[0].numberOfQuestions;
                    const second = this.topicsData[1].numberOfQuestions;
                    if (first === undefined || second === undefined || first === second) {
                        return [30, 30];
                    }
                    return first > second ? [30, 20] : [20, 30];
                })();

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                meta.data.forEach((bar: any, index: number) => {
                    const model = bar.getProps(['x', 'y', 'base'], true);
                    const barWidth = thicknesses[index];
                    const halfHeight = barWidth / 2;
                    const centerY = model.y;
                    const left = model.base;
                    const right = model.x;
                    const barLength = right - left;
                    const borderRadius = barLength > 0 ? 6 : 0;
                    const fillColor = Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor[index] as string : dataset.backgroundColor as string;

                    ctx.save();
                    ctx.fillStyle = fillColor;
                    ctx.beginPath();
                    if (barLength > 0) {
                        ctx.moveTo(left, centerY - halfHeight);
                        ctx.lineTo(right - borderRadius, centerY - halfHeight)
                        ctx.arcTo(right, centerY - halfHeight, right, centerY - halfHeight + borderRadius, borderRadius)
                        ctx.lineTo(right, centerY + halfHeight - borderRadius);
                        ctx.arcTo(right, centerY + halfHeight, right - borderRadius, centerY + halfHeight, borderRadius);
                        ctx.lineTo(left, centerY + halfHeight);
                        ctx.closePath();
                        ctx.fill();
                    }
                    ctx.restore();
                });
                return false;
            }
        }
    }
}
